<template lang="pug">
	.carto-map-component
		.map-holder
			.map
			.actions-group.flex-row.space-large(v-show="actionsVisible").align-start
				.flex-col.space-large(v-if="searchPlaceEnabled")
					DxTextBox.region-input(
						v-model="searchText",
						ref="regionInput",
						:placeholder="$tCaption('Start typing to find place..')",
						:width="240"
						:height="36"
					)
				slot(name="search-actions")
			.buttons-group(v-show="actionsVisible")
				slot(name="actions")
			slot(name="map")
</template>

<script>
	import _ from 'lodash';
	import $ from 'jquery';
	import VueTypes from 'vue-types';
	import dynamic from 'dynamic';
	import api from './api.js';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import resizeObserver from 'resize-observer';

	export default {
		translationCategory: 'carto.CartoMapComponent',
		name: 'carto-map-component',
		components: {
			DxTextBox,
			DxButton,
			DxSelectBox
		},
		props: {
			mapPosition: VueTypes.object.def({}),
			layers: VueTypes.arrayOf(VueTypes.oneOfType([
				VueTypes.shape({
					fillColor: String,
					lineColor: String,
					query: String,
					customStyle: String,
					click: Function,
					featureClickColumns: Array,
					textField: String
				}),
				VueTypes.any
			])),
			showFilters: VueTypes.bool.def(true),
			boundingBox: VueTypes.any.def(null),
			circleDrawings: VueTypes.array.def([]),
			geoJsonDrawings: VueTypes.array.def([]),
			geoJsonStyle: VueTypes.any.def(null),
			filters: VueTypes.array.def(['polygon', 'circle']),
			searchPlaceEnabled: VueTypes.bool.def(false),
			radius: VueTypes.number.def(3),
			actionsVisible: VueTypes.bool.def(true),
			addresses: VueTypes.array.def([])
		},
		data() {
			return {
				drawing: false,
				filtersDrawnItems: null,
				circleFilterData: null,
				polygonFilterData: null,
				cartoLayers: [],
				autocomplete: null,
				drawnItems: null,
				searchText: null,
				addressesGeoJsons: []
			};
		},
		computed: {},
		watch: {
			circleFilterData() {
				this.$emit('filter-changed', 'circle', this.circleFilterData);
			},
			polygonFilterData() {
				this.$emit('filter-changed', 'polygon', this.polygonFilterData);
			},
			layers: {
				handler() {
					if (__DEBUG__)
						console.info('Layers changed');
					this.buildLayers();
				},
				deep: true
			},
			mapPosition(v) {
				this.setMapPosition(v);
			},
			boundingBox(v) {
				this.setBoundingBoxView(v);
			},
			geoJsonDrawings: {
				handler() {
					if (__DEBUG__)
						console.info('GeoJsonDrawings changed');
					this.buildGeoJsonDrawings();
				},
				deep: true
			},
			circleDrawings: {
				handler() {
					if (__DEBUG__)
						console.info('CircleDrawings changed');
					this.buildGeoJsonDrawings();
				},
				deep: true
			},
			addresses() {
				this.buildAddresses().then(() => {
					this.buildGeoJsonDrawings();
					this.fitDrawnItems();
				});
			}
		},
		mounted() {
			if (__DEBUG__)
				window.cartoMapComponent = this;
			api.clearClient();
			window.jQuery = $;
			dynamic.loadOnce([
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.js',
				'https://unpkg.com/leaflet@1.5.1/dist/leaflet.css',
			]).then(() => {
				var sources = [
					'https://libs.cartocdn.com/carto.js/v4.2.0/carto.min.js',
					'https://carto.com/developers/carto-js/examples/maps/public/style.css',
					'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.js',
					'https://cdnjs.cloudflare.com/ajax/libs/leaflet.draw/0.4.13/leaflet.draw.css'
				];
				if (this.searchPlaceEnabled) {
					sources.push('https://maps.googleapis.com/maps/api/js?key=AIzaSyBxX6xc0TryC8oGKqVLaIIyJT9w46OYsLE&libraries=places,geometry&language=en&js');
				}
				dynamic.loadOnce(sources).then(() => {
					this.buildMap();
					this.buildLayers();
					setImmediate(async () => {
						if (this._isDestroyed) return;
						await this.buildAddresses();
						this.buildGeoJsonDrawings();
						if (this.circleDrawings.length || this.geoJsonDrawings.length || this.addressesGeoJsons?.length)
							this.fitDrawnItems();
						else if (!this.mapPosition?.lat) {
							this.fitUserLocation();
						}
					});
				});
			});

			this.$unobserve = resizeObserver.observe(this, this.$el, this.onResize);
		},
		destroyed() {
			delete this.$layerZipSelection;
			delete this.$map;
			if (this.$unobserve)
				this.$unobserve();
			delete this.$unobserve;
		},
		methods: {
			async buildAddresses() {
				if (!this.addresses?.length) {
					this.addressesGeoJsons = [];
					return;
				}
				const newAddresses = (await Promise.all(this.addresses.map(f => this.$Data.getLocationGeoJson({ q: f }))))
					.filter(f => f?.length && f[0]?.geojson?.type?.toLowerCase() !== 'point')
					.map(f => f && f[0]?.geojson).filter(f => f);

				if (__DEBUG__)
					console.debug('Addresses', { newAddresses });
				this.addressesGeoJsons = newAddresses;
			},
			clearFilters() {
				this.filtersDrawnItems.clearLayers();
				this.circleFilterData = null;
				this.polygonFilterData = null;
			},
			fitDrawnItems() {
				if (__DEBUG__) console.debug('Fit drawn items', this.drawnItems);
				const bounds = this.drawnItems.getBounds();
				if (!bounds.isValid()) return;
				setImmediate(() => {
					this.$map.fitBounds(bounds);
				});
			},
			buildGeoJsonDrawings() {
				if (__DEBUG__)
					console.info('buildGeoJsonDrawings', { geo: this.geoJsonDrawings, circles: this.circleDrawings });

				if (!this.drawnItems) return;
				this.drawnItems.clearLayers();
				const L = window.L;

				this.addressesGeoJsons.map(geoJson => {
					const layer = L.geoJson(geoJson, {
						style: _.assign({
							weight: 1,
							color: '#bbb',
							fill: '#bbb',
							fillOpacity: 0.35
						}, this.geoJsonStyle || geoJson),
						onEachFeature: (feature) => {
						}
					});
					layer.addTo(this.drawnItems);
				});

				this.geoJsonDrawings.map(geoJson => {
					const layer = L.geoJson(geoJson, {
						style: _.assign({
							weight: 1,
							color: '#bbb',
							fill: '#bbb',
							fillOpacity: 0.35
						}, this.geoJsonStyle || geoJson),
						onEachFeature: (feature) => {
						}
					});
					layer.addTo(this.drawnItems);
				});

				this.circleDrawings.map(circleDrawing => {
					L.circle(
							[circleDrawing.lat, circleDrawing.lng],
							circleDrawing.radius,
							{
								weight: circleDrawing.weight || 1,
								color: circleDrawing.color || '#bbb',
								opacity: circleDrawing.opacity || 1,
								fill: circleDrawing.fill || '#bbb',
								fillOpacity: circleDrawing.fillOpacity || 0.35
							},
						)
						.addTo(this.drawnItems);
				});
			},
			setBoundingBoxView(v) {
				if (__DEBUG__)
					console.info('Set bounding box', { v });
				if (v && v.length === 4)
					this.$map.fitBounds([
						[v[0], v[2]],
						[v[1], v[3]]
					]);
				else if (v && v.length === 2)
					this.$map.fitBounds(v);
			},
			buildLayers() {
				if (!this.$client) return;
				if (__DEBUG__)
					console.log('Building layers', { layers: this.layers });

				this.cartoLayers.map(f => {
					this.$client.removeLayer(f);
				});

				this.cartoLayers = [];
				const carto = window.carto;
				const client = this.$client;

				(this.layers || []).map(f => {
					if (f.query) {
						const source = this.$source = new carto.source.SQL(f.query);
						const layerCss = new carto.style.CartoCSS(f.customStyle || `
							#layer[zoom>8] {
								polygon-fill: ${f.fillColor || '#ddd'};
								polygon-opacity: 0.1;
							}
							#layer::outline[zoom>8] {
								line-width: 1;
								line-color: ${f.lineColor || '#fff'};
								line-opacity: 0.2;
							}
							#layer::labels[zoom>8] {
								text-name: [${f.textField}];
								text-face-name: 'DejaVu Sans Book';
								text-size: 10;
								text-fill: #FFFFFF;
								text-label-position-tolerance: 0;
								text-halo-radius: 1;
								text-halo-fill: #333;
								text-dy: -10;
								text-allow-overlap: false;
								text-placement: point;
								text-placement-type: dummy;
							}
						`);

						const layer = new carto.layer.Layer(source, layerCss, {
							featureClickColumns: ['cartodb_id', ...(f.featureClickColumns || [])]
						});

						client.addLayer(layer);

						this.cartoLayers.push(layer);

						if (f.click) {
							layer.on('featureClicked', featureEvent => {
								f.click(featureEvent);
							});
						}
					}
				});

				this.$client.getLeafletLayer().addTo(this.$map);
			},
			setMapPosition(v) {
				if (v)
					this.$map.setView([v.lat || 37.8, v.lng || -96], v.zoom || 5);
			},
			buildSearchPlace() {
				this.autocomplete = new google.maps.places.Autocomplete(
					($(this.$el).find('.region-input input')[0]),
					{
						//componentRestrictions: { 'country': 'us' }
					}
				);
				this.autocomplete.addListener('place_changed', this.addPlace);
				$(this.$el).find('.region-input input').attr('placeholder', ' ');
			},
			addPlace() {
				let place = this.autocomplete.getPlace();
				if (place.geometry) {
					const lat = parseFloat(place.geometry.location.lat());
					const lng = parseFloat(place.geometry.location.lng());
					const radius = this.radius * api.metricMultiplier();
					if ('utc_offset' in place) {
						delete place.utc_offset;
					}
					if (__DEBUG__)
						console.log('Place changed', { place, lat, lng, radius, searchText: this.searchText });
					const location = {
						place,
						radius,
						lat,
						lng
					};
					this.$emit('place-changed', location, $(this.$refs.regionInput.$el).find('input').val());
					if (__DEBUG__)
						window.place = location;
				}
			},
			buildMap() {
				if (__DEBUG__)
					console.log('Build map carto');

				const L = window.L;
				const carto = window.carto;

				const map = this.$map = L.map($(this.$el).find('.map')[0]).setView([this.mapPosition.lat || 37.8, this.mapPosition.lng || -96], this.mapPosition.zoom || 5);

				L.tileLayer(
					'https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}@2x.png',
					{ maxZoom: 100 }
				).addTo(map);

				this.$client = api.getClient();

				this.$currentLayer = this.$client.getLeafletLayer();
				this.$currentLayer.addTo(map);

				this.drawnItems = L.featureGroup().addTo(map);
				this.filtersDrawnItems = L.featureGroup().addTo(map);

				if (this.searchPlaceEnabled)
					this.buildSearchPlace();

				//polygon filter

				if (this.showFilters) {
					// create the polygon filter and add it to the dataviews
					let polygonFilter;
					let drawnItems = this.filtersDrawnItems;

					const createAndBindPolygonFilter = () => {
						polygonFilter = new carto.filter.Polygon();
						polygonFilter.setPolygon({
							type: 'Polygon',
							coordinates: []
						});
					};
					const preparePolygonDrawing = () => {
						// Control to draw a Polygon and use it as the spatial filter
						let drawControl = new L.Control.Draw({
							draw: {
								polygon: {
									allowIntersection: false,
									showArea: true,
									shapeOptions: {
										color: '#f27649'
									}
								},
								polyline: false,
								line: false,
								marker: false,
								rectangle: false,
								circle: false,
								circlemarker: false,
							},
							edit: false
						});
						map.addControl(drawControl);

						// Get radius and center & apply to Polygon filter
						map.on(L.Draw.Event.CREATED, (e, d) => {
							if (__DEBUG__)
								console.log('Set polygon filter', { e, args: arguments });
							if (e.layerType === 'polygon') {
								//widgets.map(f => f.addFilter(polygonFilter));
								//orFilter.addFilter(polygonFilter);

								let drawnPolygon = e.layer;
								drawnItems.clearLayers();
								drawnItems.addLayer(drawnPolygon);

								// get polygon data
								let polygonData = drawnPolygon.toGeoJSON()['geometry'];
								polygonFilter.setPolygon(polygonData); // updated filter !*/

								this.polygonFilterData = polygonData;
							}
						});

					};

					if (this.filters.indexOf('polygon') > -1) {
						createAndBindPolygonFilter();
						preparePolygonDrawing();
					}

					if (this.filters.indexOf('circle') > -1)
						this.createCircleFilter();
				}

				this.$emit('built');
			},
			createCircleFilter() {
				let circleFilter = null;
				let drawnItems = this.filtersDrawnItems;
				const carto = window.carto;
				const L = window.L;
				const map = this.$map;

				// create the circle filter and add it to the dataviews
				const createAndBindCircleFilter = () => {
					circleFilter = new carto.filter.Circle();
					circleFilter.setCircle({
						lat: 0,
						lng: 0,
						radius: 0
					});
				};
				const prepareCircleDrawing = () => {
					// Control to draw a Circle and use it as the spatial filter
					let drawControl = new L.Control.Draw({
						draw: {
							polygon: false,
							polyline: false,
							line: false,
							marker: false,
							rectangle: false,
							circle: {
								shapeOptions: {
									color: 'red',
									weight: 0.1,
									opacity: 0.5
								}
							},
							circlemarker: false,
						},
						edit: false
					});
					map.addControl(drawControl);

					map.on(L.Draw.Event.DRAWSTART, (e, d) => {
						this.drawing = true;
					});

					map.on(L.Draw.Event.DRAWSTOP, (e, d) => {
						//this.drawing = true;
						setTimeout(() => {
							if (this._isDestroyed) return;
							this.drawing = false;
						}, 500);
					});

					// Get radius and center & apply to Circle filter
					map.on(L.Draw.Event.CREATED, (e, d) => {
						if (__DEBUG__)
							console.info('Circle drawn', { e, args: arguments });
						if (e.layerType === 'circle') {
							//widgets.map(f => f.addFilter(circleFilter));

							let drawnCircle = e.layer;
							drawnItems.clearLayers();
							drawnItems.addLayer(drawnCircle);

							// get circle data
							let radius = drawnCircle.getRadius();
							let centerLat = drawnCircle.getLatLng().lat;
							let centerLng = drawnCircle.getLatLng().lng;

							const circleData = {
								lat: centerLat,
								lng: centerLng,
								radius: radius
							};

							circleFilter.setCircle(circleData); // updated filter !
							drawControl._toolbars.draw.disable();

							this.circleFilterData = circleData;
						}
					});
				};

				createAndBindCircleFilter();
				prepareCircleDrawing();
			},
			setInput(value) {
				this.$refs.regionInput.instance.option('value', value)
				$(this.$refs.regionInput.$el).find('input').val(value);
			},
			onResize() {
				if (__DEBUG__)
					console.debug('CartoMap on resize');
				this.$map._onResize();
			},
			async fitUserLocation() {
				let userLocationInfo = await this.$auth.getUserLocation();

				const bounds = userLocationInfo.bounds;
				if (bounds) {
					this.$map.fitBounds(
						[
							[bounds[0], bounds[2]],
							[bounds[1], bounds[3]]
						]
					);
				}
			}
		}
	};
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.carto-map-component {
		.map-holder {
			height: 100%;
			width: 100%;
			min-height: 240px;
			margin-top: 0px;
			position: relative;

			.map {
				width: 100%;
				height: 100%;
				min-height: 240px;
				min-width: 240px;
			}

			> .actions-group {
				position: absolute;
				left: 60px;
				top: 10px;
				z-index: 1000;
				padding: 10px;
				background: rgba(255, 255, 255, 0.75);
				box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
				border-radius: 4px;
			}

			> .buttons-group {
				z-index: 1000;
				position: absolute;
				left: 10px;
				bottom: 10px;
			}

			.region-input {

			}
		}
	}

	.pac-container {
		z-index: 2000 !important;
	}
</style>
